import type { ClassDefinition } from '../../schemas/classDefinition';
import { sciamanoAdvanced2to5Moves } from '../moves/sciamano/advanced-2-5';
import { sciamanoAdvanced6to10Moves } from '../moves/sciamano/advanced-6-10';
import { sciamanoStartingAutoMoves, sciamanoStartingChoiceMoves } from '../moves/sciamano/initial';

/**
 * Definizione della classe **Sciamano** (manuale V1, rr. 5443-5767).
 *
 * Decima classe trascritta. Pattern principali stress-testati:
 *
 * - **Mistica diversa dal casting standard**: lo Sciamano dialoga con
 *   l'Aldilà (spiriti, antenati) tramite la mossa-tiro *Aiuto dall'Altro
 *   Mondo* a prese — pattern coerente con *Meditazione* del Monaco e *Aura*
 *   del Paladino. **Non è casteggiante**: `spellcasting` resta `undefined`,
 *   come Bardo/Guerriero/Ladro/Monaco/Ranger. Il sistema `getActiveSpellLists`
 *   opera sui `moves` del draft (non su questo campo): lo Sciamano vede il
 *   placeholder "non casteggiante" nella `SectionSpellSelection`, esattamente
 *   come Monaco/Ranger.
 *
 * - **`maxBonds: 5`** — nuovo estremo alto del range FI-3 (precedente max: 4
 *   sul Druido; estremo basso: 3 su Mago/Paladino). Range finora osservato:
 *   3-4-5-6 (Bardo a 6).
 *
 * - **`baseHp: 9`** — valore non ancora usato fra le 9 classi precedenti
 *   (range osservato: 6-7-8-10).
 *
 * - ***Che Tu Sia...*** (D1): mossa-config L2-5 con `MoveSchema.choices` su 2
 *   opzioni (benevolenza/malevolenza), `choiceCount: 1`. Pattern coerente con
 *   altre mosse-config con scelta strutturata al momento della presa.
 *
 * - **FI-8 stub description-only** (D3): *Animare Zombi*, *Apocalisse Zombi*,
 *   *Collezionista di Teschi*, *Uno con lo Spirito* — pseudo-gregari. 3 di
 *   queste hanno `prerequisites: ['sciamano.aiuto-dall-altro-mondo']`
 *   strutturato per facilitare il refactor unificato Ranghi 1-3 nella PR FI-8
 *   post-12 classi.
 *
 * - **Bambole e Pendoli** (D2): description-only, pattern stabilito a 4
 *   occorrenze precedenti (*Allargare le Basi* Chierico, *Mille Risorse*
 *   Ladro, *Di Tutto un Po'* Bardo, *Formazione Eterogenea* Mago). Tema di
 *   avanzamento livello, gestito a runtime al level-up.
 *
 * - **Mosse multiclasse** (FI-17): *Amico degli Spiriti* (L2-5) e *Figlio
 *   degli Spiriti* (L6-10) scelgono da Barbaro o Druido. L'archetipo Elfo
 *   estende il pool al Mago. Description-only.
 *
 * - **Forward-refs cross-class TARGET**: *Portavoce dell'Aldilà* è
 *   referenziata in `prerequisites.anyOf` da 2 mosse del Druido. Ora che lo
 *   Sciamano è trascritto l'ID esiste localmente nel catalogo aggregato di
 *   `src/data/moves/index.ts` — il `knownExternalIds` nei test del Druido
 *   è stato aggiornato (D4).
 *
 * **Punti notevoli**:
 * - Dado di danno **d6**, PF base **9**, Carico base **8**
 * - 5 archetipi razza-tematici
 * - 3 mosse iniziali automatiche + 3 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (41 mosse totali)
 * - 5 template di Legami (= `maxBonds`, FI-3 rispettato — nuovo estremo alto)
 * - 4 categorie di Aspetto (Occhi/Capelli/Vesti/Corpo)
 */
export const sciamanoClassDefinition: ClassDefinition = {
  id: 'sciamano',
  name: 'Sciamano',

  intro:
    "Il vostro mondo… voi lo vedete piatto, limitato. Ma io vedo *oltre*! Sento pulsare l'Altro Mondo, un regno che voi ignorate, eppure vi avvolge. Lì dimorano gli Spiriti dell'Aldilà, entità che chiamate Loa, Numi, o con mille altri nomi, ma che per me sono realtà viva, pulsante. Sono il Padre Ombra, la Morte; la Danzatrice dei Dadi, la Fortuna; il Saggio Capostipite, gli Antenati. Sono ovunque, e a volte si manifestano nel mondo tangibile attraverso simboli e rituali, riversando il loro potere nei miei feticci. Io conosco i loro nomi, la loro essenza, i loro voleri.\n\nVoi vi rivolgete a dèi lontani, silenti nel loro etere. Noi, Sciamani, dialoghiamo con la forza viva che anima ogni cosa, ottenendo il favore (o la collera) degli Spiriti dell'Aldilà. Un tempo, questo era sapere comune. Si invocavano gli Antenati per la loro saggezza e la protezione, si ottenevano presagi dal mondo degli spiriti, si animavano persino i morti per farsi servire. Ora, voi avete dimenticato. Ma io ricordo. Io oso aprire i varchi tra i mondi, manipolare il velo che li separa, là dove voi tremate.\n\nDanzo al ritmo ancestrale del tamburo. Invoco gli spiriti, offro loro doni, a volte persino una parte di me, per placarli e ingraziarli affinché mi concedano il loro favore... o manifestino la loro collera. E per questo pago il costo, *sempre*. Sangue versato, l'integrità stessa del mio corpo e della mia mente, tempo prezioso, sacrifici o favori futuri: tutto questo è il sentiero sciamanico. Potere immenso fluisce dall'Aldilà, un potere che ho imparato a invocare e dirigere. Mia è la responsabilità. Mia la scelta. Siate saggi, perché l'Aldilà… attende.",

  // Manuale V1, rr. 5452-5456
  suggestedNames: [
    // Umano
    'Beras',
    'Daron',
    'Hawl',
    'Ilko',
    'Liss',
    'Nether',
    'Mall',
    'Pult',
    'Rigga',
    'Monray',
    'Halius',
    'Phisi',
    // Elfo
    'Rielha',
    'Asion',
    'Vrestos',
    'Inyb',
    'Lithpay',
    'Bachphas',
    'Ryros',
    'Baelja',
    'Dana',
    'Phanson',
    // Nano
    'Borol',
    'Dal',
    'Gird',
    'Jan',
    'Kard',
    'Panek',
    'Serc',
    'Tarik',
    'Ullan',
    'Urk',
    'Nosli',
    'Abe',
    'Sonne',
    // Halfling
    'Morty',
    'Flashwit',
    'Keeper',
    'Kon',
    'May',
    'Quick',
    'Sticks',
    'Tannerock',
    'Toc',
    'Wren',
    // Mezz'orco
    'Lixha',
    'Manio',
    "Mo'mic",
    'Beelpai',
    'Draal',
    'Bela',
    'Garoth',
    'Vecer',
    'Saba',
    'Luso',
    'Mau',
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 5458-5464
  aspetti: [
    {
      label: 'Occhi',
      options: ['Saggi', 'Irridenti', 'Morti'],
    },
    {
      label: 'Capelli',
      options: ['Selvaggi', 'Intrecciati', 'Calvo'],
    },
    {
      label: 'Vesti',
      options: ['Pellicce consumate', 'Vesti da viaggio', 'Abito cerimoniale'],
    },
    {
      label: 'Corpo',
      options: ['Magro', 'Anziano', 'Tatuato'],
    },
  ],

  // Manuale V1, rr. 5466-5472
  damageDie: 'd6',
  baseHp: 9,
  baseLoad: 8,

  // Manuale V1, rr. 5474-5482
  alignmentPrecepts: {
    buono: 'Aiuti uno spirito tormentato a ritrovare la pace.',
    legale: 'Ti metti in pericolo per soddisfare i tuoi antenati.',
    neutrale: 'Concedi ai morti i riti funebri.',
    caotico: 'Convinci gli altri a rompere con la tradizione.',
    malvagio: 'Tormenti uno spirito per puro divertimento.',
  },

  // Manuale V1, rr. 5500-5502. **maxBonds: 5** (5 templates ↔ 5, FI-3 rispettato).
  // Nuovo estremo alto del range FI-3 (precedente max: Druido = 4).
  bondTemplates: [
    'Uno degli antenati di _____ conosceva uno dei miei, devo scoprire cosa li legava.',
    'Gli spiriti mi hanno detto di stare in guardia da _____, ma si rifiutano di dirmene la ragione.',
    '_____ è giovane e inesperto. Gli mostrerò quanto vale la saggezza degli antichi.',
    'Sebbene il popolo di _____ e il mio siano agli antipodi, hanno molto in comune: dobbiamo unire le forze.',
    "_____ è un «faro» nell'aldilà. Devo proteggerlo o sfruttarlo?",
  ],
  maxBonds: 5,

  // Manuale V1, rr. 5504-5512
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        "Quando usi una presa di *Aiuto dall'Altro Mondo* contro una creatura, puoi usarne una in più e infliggere al bersaglio 1d6 danni (solo 1d6, senza altri bonus), oltre all'effetto di base.",
      grantedMoveId: 'sciamano.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Quando prendi *Amico degli Spiriti* o *Figlio degli Spiriti*, puoi scegliere anche tra le mosse del Mago.',
      grantedMoveId: 'sciamano.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description:
        'Quando sei sottoterra, nessuno spirito attaccherà te o chi è sotto la tua protezione, a meno che non venga attaccato per primo.',
      grantedMoveId: 'sciamano.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        'Quando evochi il fantasma di un antenato per assisterti, raggiungi sempre la persona giusta.',
      grantedMoveId: 'sciamano.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        'Quando una delle tue mosse richiede di prendere danno, puoi mangiare il cuore di una creatura appena uccisa e ridurre di 2 il danno subito.',
      grantedMoveId: 'sciamano.archetipo-mezzorco',
    },
  ],

  startingMoves: sciamanoStartingAutoMoves.map((m) => m.id),

  // Manuale V1, r. 5541: "*Inoltre, scegli una delle seguenti mosse:*"
  startingChoiceGroups: [
    {
      id: 'mossa-iniziale-aggiuntiva',
      name: 'Mossa iniziale aggiuntiva',
      description:
        'Scegli una di queste tre mosse: *Medium* (estende *Onorevoli Padri* a anime non-antenate), *Auspicio* (interpreti i segni degli spiriti) o *Radici Profonde* (declami conoscenze sul tuo popolo con SAG anziché INT).',
      moveOptions: sciamanoStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: sciamanoAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: sciamanoAdvanced6to10Moves.map((m) => m.id),

  // Lo Sciamano è **non casteggiante**: la sua mistica si basa sull'Aldilà
  // (spiriti, antenati, costi narrativi: sangue, integrità, favori futuri),
  // NON sul sistema *Lanciare Incantesimi* / *Mana* / Tradizioni Sacre. Le 5
  // classi casteggianti DWR sono Mago, Chierico, Druido, Warlock, Paladino.
  // Coerenza con la nota architetturale di `SpellcastingConfigSchema`.
  // spellcasting resta undefined.

  // Manuale V1, rr. 5485-5498
  startingEquipment: {
    always: [
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
      {
        id: 'attrezzatura-da-avventuriero',
        name: 'Attrezzatura da Avventuriero',
        weight: 2,
        uses: 5,
        tags: [],
      },
      {
        id: 'pugnale-sacrificale-di-selce',
        name: 'Pugnale sacrificale di selce',
        weight: 1,
        tags: ['corta'],
      },
    ],
    choices: [
      {
        id: 'difese-iniziali',
        prompt: "Scegli un'opzione per le tue difese",
        pickCount: 1,
        options: [
          {
            id: 'pelli-conciate',
            label: 'Pelli conciate',
            items: [
              {
                id: 'pelli-conciate',
                name: 'Pelli conciate',
                weight: 1,
                armor: 1,
                tags: ['indossato'],
              },
            ],
          },
          {
            id: 'vesti-cerimoniali-pozione',
            label: 'Vesti cerimoniali + Pozione Curativa',
            items: [
              {
                id: 'vesti-cerimoniali',
                name: 'Vesti cerimoniali',
                weight: 0,
                tags: ['indossato'],
              },
              {
                id: 'pozione-curativa',
                name: 'Pozione Curativa',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
        ],
      },
      {
        id: 'armi-iniziali',
        prompt: "Scegli un'opzione per le tue armi",
        pickCount: 1,
        options: [
          {
            id: 'coltello-lungo-olio',
            label: 'Coltello lungo + Olio di Tagete',
            items: [
              {
                id: 'coltello-lungo',
                name: 'Coltello lungo',
                weight: 1,
                tags: ['media'],
              },
              {
                id: 'olio-di-tagete',
                name: 'Olio di Tagete',
                weight: 0,
                uses: 1,
                tags: ['pericoloso', 'applicato'],
              },
            ],
          },
          {
            id: 'bastone-erba-pipa',
            label: 'Bastone + Erba Pipa degli Halfling',
            items: [
              {
                id: 'bastone',
                name: 'Bastone',
                weight: 1,
                tags: ['media', 'a due mani'],
              },
              {
                id: 'erba-pipa-degli-halfling',
                name: 'Erba Pipa degli Halfling',
                weight: 0,
                uses: 6,
                tags: [],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'razioni-monete',
            label: 'Razioni da Dungeon + 7 monete',
            items: [
              {
                id: 'razioni-da-dungeon-extra',
                name: 'Razioni da Dungeon',
                weight: 1,
                uses: 5,
                tags: ['razione'],
              },
              {
                id: 'monete',
                name: 'Monete',
                weight: 0,
                quantity: 7,
                tags: [],
                description: 'Soldi del PG.',
              },
            ],
          },
          {
            id: 'erbe-e-unguenti',
            label: 'Erbe e Unguenti',
            items: [
              {
                id: 'erbe-e-unguenti',
                name: 'Erbe e Unguenti',
                weight: 1,
                uses: 2,
                tags: ['lento'],
              },
            ],
          },
          {
            id: 'farina-d-ossa',
            label: "Farina d'ossa",
            items: [
              {
                id: 'farina-d-ossa',
                name: "Farina d'ossa",
                weight: 1,
                uses: 3,
                tags: [],
                description:
                  "Utile per placare uno spirito adirato (max 3 utilizzi). Riferimento mossa *Portavoce dell'Aldilà*.",
              },
            ],
          },
        ],
      },
    ],
  },
};
